import {
  getImageWithSize,
  hasSubscriptionOption,
  isTouchDevice,
  handleize,
  deliveryFrequencyNote,
  getPerUnitPrice,
  formatPrice,
  uniqueNumber
} from 'lib/util'
import Cart from 'lib/cart'
import 'modules/yotpo-bottomline/yotpo-bottomline.vue'
import state from 'lib/appState'

export default {
  computed: {
    primaryTagsMatrix () {
      return window.HU.primary_tags_matrix.filter(t => t.enabled)
    },
    isOnPreorder () {
      return this.tags.includes('PREORDER')
    },
    customTag () {
      const primaryTagGroup = this.primaryTagsMatrix.reduce((match, tagGroup) => {
        if (match) return match
        if (tagGroup.tags.find((tag) => this.tags.includes(tag))) {
          return tagGroup
        }
        return match
      }, null)
      return primaryTagGroup
    },
    bundleIdentifier () {
      return this.tags.reduce((match, tag) => {
        if (match) return match
        if (tag.includes('BI:')) return tag.replace('BI:', '')
        return match
      }, null)
    },
    packSizes () {
      return this.hasSizes ? this['Gift amount'] ? this['Gift amount'] : this['Size'] || this['Quantity'] || [] : []
    },
    isGiftCard () {
      return handleize(this.type) === 'gift-card'
    },
    hasSizes () {
      if (this['Gift amount']) return this['Gift amount'].length
      if (this['Quantity']) return this['Quantity'].length
      return this['Size'] && this['Size'].length
    },
    firstOption () {
      return (this.options || [])[0]
    },
    optionName () {
      if (this.type === 'Cookies') {
        return (this.optionNames || [])[1]
      }
      return (this.optionNames || [])[0]
    },
    activeOptions () {
      if (this.type === 'Cookies') {
        return this.variants.reduce((options, variant) => options.concat(variant.option2), [])
      }
      return this.packSizes.length ? this.packSizes : this[this.optionName]
    },
    getVariantImageIndex () {
      if (this.selectedFlavor) {
        const matchingVariant = this.variants.find(v => v['option2'] === this.selectedFlavor || v['option1'] === this.selectedFlavor)
        if (matchingVariant) {
          return this.imageWithDetails.findIndex(image => matchingVariant.sku && image.alt && image.alt.includes(matchingVariant.sku))
        }
      }
      return -1
    },
    getVariantImage () {
      if (this.getVariantImageIndex >= 0) {
        if (this.imageWithDetails[this.getVariantImageIndex]) {
          const matchingImage = this.imageWithDetails[this.getVariantImageIndex]
          if (matchingImage) return matchingImage.src
        }
      }
      return this.images[0]
    },
    imageSrc () {
      return getImageWithSize(this.getVariantImage, 409)
    },
    imageSources () {
      const imageLarge = this.getVariantImage
      const imageNormal = this.getVariantImage
      const imageXSmall = this.getVariantImage

      return [
        {
          media: 'all',
          srcset: `${imageLarge} 2160w, ${imageNormal} 1080w, ${imageXSmall} 640w, ${imageXSmall} 320w`
        }
      ]
    },
    imageHover () {
      if (this.selectedFlavor && this.getVariantImageIndex >= 0) {
        const matchingImage = this.imageWithDetails[this.getVariantImageIndex + 1]
        if (matchingImage) return matchingImage.src
      }
      if (this.images.length < 2) return null
      return this.images[1]
    },
    imageSrcHover () {
      return this.imageHover ? getImageWithSize(this.imageHover, 409) : null
    },
    imageSourcesHover () {
      const imageLarge = getImageWithSize(this.imageHover, 850)
      const imageNormal = getImageWithSize(this.imageHover, 640)
      const imageXSmall = getImageWithSize(this.imageHover, 500)

      return [
        {
          media: 'all',
          srcset: `${imageLarge} 2160w, ${imageNormal} 1080w, ${imageXSmall} 640w, ${imageXSmall} 320w`
        }
      ]
    },
    link () {
      if (this.is_amazon_ship && this.amazon_link) return this.amazon_link
      return `/products/${this.handle}${this.activeVariantUrl}`
    },
    activeVariant () {
      return this.variants.find(({title = '', id}) => {
        return ~title.indexOf(this.activeOption)
      })
    },
    activeVariantUrl () {
      return (this.activeVariant ? `?variant=${this.activeVariant.id}` : '')
    },
    hasSubscription () {
      return hasSubscriptionOption(this)
    },
    formattedCompareAtPrice () {
      return formatPrice(this.activeVariant.compare_at_price, 2)
    },
    formattedPrice () {
      return formatPrice(this.activeVariant.price, 2)
    },
    isOnSale () {
      return this.variants.reduce((onSale, variant) => {
        if (onSale) return onSale
        if (variant.compare_at_price) {
          return variant.price !== variant.compare_at_price
        }
        return onSale
      }, false)
    },
    isSimpleProduct () {
      return this.firstOption === 'Title'
    },
    subscriptionMode () {
      return this.hasSubscription && !!this.isSubscriptionMode
    },
    subscriptionData () {
      const {
        subscription = null
      } = this

      if (!subscription || !subscription.subscription_id) throw new Error('Subscription data missing')
      const intervals = subscription.shipping_interval_frequency.split(',')
      const intervalUnits = subscription.shipping_interval_unit_type
      const variant = this.getVariantForSize(this.activeOption)

      return {
        original_variant_id: variant.id,
        is_subscription: true,
        shipping_interval_frequency: intervals[0],
        shipping_interval_unit_type: intervalUnits,
        subscription_id: subscription.subscription_id,
        discount_percentage: parseFloat(subscription.discount_percentage)
      }
    },
    subscriptionFrequiencyNote () {
      if (!this.subscriptionMode) return null
      return deliveryFrequencyNote(this.subscriptionData)
    },
    perUnitPrice () {
      const priceLine = getPerUnitPrice(this.activeVariant)
      return priceLine
    },
    bundleVariants () {
      return Array.isArray(this.bundle_variants) ? this.bundle_variants : []
    },
    preorderCartNote () {
      return this.tags.reduce((message, tag) => {
        if (message) return message
        if (tag.includes('PREORDER_CART_NOTE')) {
          return tag.replace('PREORDER_CART_NOTE::', '')
        }
        return null
      }, null)
    },
    baseProductProps () {
      const base = {}
      if (this.isOnPreorder) {
        base['_preorderItem'] = true

        if (this.preorderCartNote) {
          base['_preorderCartNote'] = this.preorderCartNote
        }
      }
      return base
    },
    frequencyIntervals () {
      if (Object.keys(this.subscription).length) {
        return this.subscription.shipping_interval_frequency.split(',')
      }
    },
    getPropertiesOnTime () {
      if (Object.keys(this.subscription).length) {
        const { shipping_interval_frequency, shipping_interval_unit_type, subscription_id, discount_percentage } = this.subscriptionData
        const subscriptionPrice = this.activeVariant.price - (this.activeVariant.price * Number(discount_percentage) / 100)
        return {
          _discount_percentage: discount_percentage,
          _shipping_interval_unit_type: shipping_interval_unit_type,
          _shipping_interval_frequency: shipping_interval_frequency,
          _subscription_price: subscriptionPrice,
          _subscription_key: subscription_id,
          _subscription_variant_id: this.activeVariant.subscriptions.discount_variant_id,
          _frequencyIntervals: this.frequencyIntervals
        }
      }
      return {}
    },
    optionPositionForSize () {
      return this.options_with_values.reduce((nameKey, option) => {
        if (nameKey) return nameKey
        if (['size', 'quantity'].includes(handleize(option.name))) return `option${option.position}`
        return nameKey
      }, null)
    },
    flavorOptionPosition () {
      const flavor = this.options_with_values.find(position => handleize(position.name) === 'flavor')
      return flavor ? flavor.position : 0
    }
  },
  methods: {
    toggleDrawer (target) {
      this.state.activeDrawer = target
    },

    changeActiveOption (value) {
      this.activeOption = value
    },

    getVariantForSize (size) {
      const option = this.optionPositionForSize
      if (!option) return this.variants[0]
      return this.variants.reduce((selectedVariant, variant) => {
        if (selectedVariant) return selectedVariant

        if (this.selectedFlavor) {
          if (variant[option] === size && variant[`option${this.flavorOptionPosition}`] === this.selectedFlavor) return variant
        }

        if (variant[option] === size) return variant
        return selectedVariant
      }, null)
    },
    addFreeGift () {
      const freeGiftVariantId = state.freeGiftVariantId
      const cartItems = state.cart.items
      const freeGiftTotalGoal = state.cart.total_price
      const freeGiftGoal = state.freeGiftGoal
      const freeGiftType = state.freeGiftType

      if (freeGiftVariantId > 0 && freeGiftType === 'goal') {
        const result = cartItems.filter(product => {
          return product.id === freeGiftVariantId
        })

        if (result.length < 1 && freeGiftTotalGoal >= freeGiftGoal) {
          let payload = [{
            id: freeGiftVariantId,
            quantity: 1
          }]
          Cart.add(payload)
        }
      }
    },
    async addToCart () {
      const activeVariant = this.activeVariant
      if (!activeVariant) return null
      if (!activeVariant.available) throw new Error('Item not available')
      try {
        this.loading = true
        if (this.subscriptionMode) {
          const { shipping_interval_frequency, shipping_interval_unit_type, subscription_id, discount_percentage, is_subscription, original_variant_id } = this.subscriptionData
          const discoutnVariantID = this.activeVariant.subscriptions ? this.activeVariant.subscriptions['discount_variant_id'] : this.activeVariant.id
          const handle = this.product.handle
          await Cart.addSubscription(discoutnVariantID, 1, shipping_interval_frequency, shipping_interval_unit_type, subscription_id, { handle, discount_percentage,is_subscription, original_variant_id })
        } else {
          const baseProperties = this.baseProductProps
          let payload = [{
            id: activeVariant.id,
            quantuty: 1,
            properties: {
              ...this.getPropertiesOnTime,
              ...baseProperties
            }
          }]

          if (this.bundleVariants.length && this.type && this.type.includes('Bundle')) {
            const bundleID = uniqueNumber()
            payload = this.bundleVariants.map((id) => {
              return {
                id,
                quantity: 1,
                properties: {
                  'For Bundle': this.title,
                  '_bundleID': bundleID,
                  '_BI': this.bundleIdentifier,
                  '_bundleProductID': this.variants[0].id
                }
              }
            })
            payload.push({
              id: this.variants[0].id,
              quantity: 1,
              properties: {
                _hide: true
              }
            })
          }
          await Cart.add(payload)
          this.addFreeGift()
        }
        // window.location = '/cart'
        window.MicroBus.emit(`side-cart:toggle`)
      } catch (error) {
        console.log(error)
      } finally {
        this.loading = false
        this.quickBuyActive = false
      }
    },

    handleAddToCart (option) {
      if (!this.isSimpleProduct) {
        this.changeActiveOption(option)
      }
      this.addToCart()
    },

    toggleQuickBuy () {
      this.quickBuyActive = isTouchDevice() && !this.quickBuyActive
    },

    onClickOutside () {
      this.quickBuyActive = false
    },

    handleClick () {
      window.location.replace(this.link)
    }
  }
}
